import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

interface UploadAssetInput {
  clientId: string;
  kind: 'icon' | 'logo';
  file: File;
}

interface UploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  objectKey: string;
  maxBytes: number;
  expiresIn: number;
}

/**
 * Upload a per-tenant asset (icon / logo). Two-step flow:
 *   1. POST /v1/clients/{id}/upload-url — Lambda mints a presigned PUT URL
 *      pinned to the right S3 key + Content-Type.
 *   2. PUT the file directly to S3 via the presigned URL.
 *
 * Returns the public CloudFront URL of the uploaded object so the caller
 * can persist it on the client record (smallLogoUrl / largeLogoUrl) via
 * the existing `PUT /v1/clients/{id}` endpoint. The hook deliberately
 * doesn't wire that DB write itself — different mutation, different
 * `onSuccess` invalidation pattern.
 *
 * Client-side size check matches the server's `maxBytes` so over-large
 * files fail before touching S3.
 */
export function useUploadTenantAssetMutation(): UseMutationResult<
  { publicUrl: string; objectKey: string },
  Error,
  UploadAssetInput
> {
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({ clientId, kind, file }) => {
      if (!api) {
        throw new Error('useUploadTenantAssetMutation: api client unavailable');
      }
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        throw new Error('Unsupported file type — please pick a PNG or JPG.');
      }

      const presigned = await api.post<UploadUrlResponse>(`/v1/clients/${clientId}/upload-url`, {
        kind,
        contentType: file.type,
      });

      if (file.size > presigned.maxBytes) {
        const cap = Math.round(presigned.maxBytes / (1024 * 1024));
        throw new Error(`File is too large — ${kind} must be ≤ ${cap} MB.`);
      }

      const uploadResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        const text = await uploadResponse.text().catch(() => '');
        throw new Error(`S3 upload failed (${uploadResponse.status}): ${text.slice(0, 200)}`);
      }

      return { publicUrl: presigned.publicUrl, objectKey: presigned.objectKey };
    },
  });
}
